import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { FilterItemDto } from '../../../../../../common/app-config/filter-models/misc';
import { declineReasons, unHireReasons } from '../../../../../../common/general-types/filters.dto';
import { MbaIcon } from '../../atoms/mba-icon/mba-icon';
import { ui } from '../../assignment-ui';

const OTHER_REASON_ID = 8;
const OTHER_MIN_LENGTH = 10;

/**
 * Decline / un-hire confirmation. Keeps the existing reason lists (and the "Other needs
 * 10+ characters" rule) so what the API stores and reports on is unchanged.
 */
@Component({
  selector: 'mbt-reason-modal',
  imports: [MbaIcon],
  template: `
    <div data-mba-modal class="fixed inset-0 z-70 grid place-items-center bg-(--mba-scrim) p-6 animate-[mba-fade_220ms_cubic-bezier(0.2,0,0,1)] motion-reduce:animate-none"
      (click)="closed.emit()">
      <div role="dialog" aria-modal="true" aria-labelledby="mba-modal-title" (click)="$event.stopPropagation()"
        class="flex max-h-full w-[min(560px,100%)] flex-col overflow-hidden rounded-2xl bg-(--mba-surface) shadow-[0_20px_48px_rgba(18,6,43,0.14)] animate-[mba-in_220ms_cubic-bezier(0.2,0,0,1)] motion-reduce:animate-none">
        <div class="flex items-center gap-3 border-b border-(--border-subtle) px-[22px] py-[18px]">
          <h2 id="mba-modal-title" class="m-0 font-(family-name:--font-display) text-[26px] leading-[0.92] font-bold tracking-[-0.02em] text-(--fg-1)">
            {{ mode() === 'decline' ? 'Decline' : 'Un-hire' }} {{ firstName().trim() }}?</h2>
          <button type="button" aria-label="Close" [class]="ui.iconButton + ' ml-auto'" (click)="closed.emit()"><mba-icon name="x" /></button>
        </div>
        <div class="flex flex-1 flex-col gap-3.5 overflow-y-auto px-[22px] py-[18px] text-sm text-(--fg-2)">
          @if (mode() === 'decline') {
            <p class="m-0">{{ fullName() }} is removed from this brief and told the spot has gone to someone else.</p>
          } @else {
            <p class="m-0">{{ fullName() }} is removed from this brief and loses access to it.
              {{ hasUploads() ? 'Videos they\u2019ve already uploaded stay in this brief.' : 'They haven\u2019t uploaded anything yet.' }}</p>
          }
          <div class="flex flex-col gap-1.5">
            <span id="mba-reason-label" class="text-xs font-bold text-(--fg-2)">Reason</span>
            <div class="flex flex-wrap gap-1.5" role="radiogroup" aria-labelledby="mba-reason-label">
              @for (reason of reasons(); track reason.id) {
                <button type="button" role="radio" [attr.aria-checked]="selected()?.id === reason.id"
                  [class]="selected()?.id === reason.id ? chipOn : chipOff" (click)="selected.set(reason)">{{ reason.name }}</button>
              }
            </div>
          </div>
          @if (isOther()) {
            <label class="flex flex-col gap-1.5">
              <span class="text-xs font-bold text-(--fg-2)">Tell us why</span>
              <textarea [value]="otherText()" (input)="onOther($event)" maxlength="500"
                class="min-h-[90px] resize-y rounded-lg border border-(--border-default) bg-(--mba-surface) px-3 py-2.5 font-(family-name:--font-fallback) text-sm text-(--fg-1) focus:border-(--app-accent) focus:outline-none focus:ring-[3px] focus:ring-(--app-accent-ring)"></textarea>
              @if (otherText().length > 0 && otherText().trim().length < otherMin) {
                <span class="text-xs text-(--danger)">At least {{ otherMin }} characters</span>
              }
            </label>
          }
        </div>
        <div class="flex items-center justify-end gap-2.5 border-t border-(--border-subtle) bg-(--bg-alt) px-[22px] py-3.5">
          <button type="button" [class]="ui.btnOutline" (click)="closed.emit()">Cancel</button>
          <button type="button" [class]="ui.btnDanger" [disabled]="!valid()" (click)="submit()">
            {{ mode() === 'decline' ? 'Decline' : 'Un-hire' }}
          </button>
        </div>
      </div>
    </div>
  `,
  host: { style: 'display: contents', '(document:keydown.escape)': 'closed.emit()' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReasonModal {
  protected readonly ui = ui;
  private readonly chip = `cursor-pointer rounded-full border px-3 py-1.5 text-[13px] font-semibold ${ui.focusRing}`;
  protected readonly chipOff = `${this.chip} border-(--border-default) text-(--fg-2)`;
  protected readonly chipOn = `${this.chip} border-(--fg-1) bg-(--fg-1) text-(--mba-surface)`;

  readonly mode = input.required<'decline' | 'unhire'>();
  readonly firstName = input('');
  readonly fullName = input('');
  readonly hasUploads = input(false);

  readonly closed = output<void>();
  readonly confirmed = output<string>();

  protected readonly otherMin = OTHER_MIN_LENGTH;
  protected readonly selected = signal<FilterItemDto | null>(null);
  protected readonly otherText = signal('');

  protected readonly reasons = computed(() => (this.mode() === 'decline' ? declineReasons : unHireReasons));
  protected readonly isOther = computed(() => this.selected()?.id === OTHER_REASON_ID);
  protected readonly valid = computed(() =>
    !!this.selected() && (!this.isOther() || this.otherText().trim().length >= OTHER_MIN_LENGTH),
  );

  protected onOther(event: Event): void {
    this.otherText.set((event.target as HTMLTextAreaElement).value);
  }

  protected submit(): void {
    const reason = this.selected();
    if (!reason || !this.valid()) {
      return;
    }
    this.confirmed.emit(this.isOther() ? this.otherText().trim() : reason.name);
  }
}
